import { bacteriaMetaConfig_9P } from '../calculate_ctvalue';
import { getBacteriaScore } from '../../utils/bacteriaEvaluator';

// =====================================================================
// Config_SCFAs.js  —  9P panel "SCFAs" summary section
// ---------------------------------------------------------------------
// Flow:
//   1. For each species, the ct value from the CSV becomes a score:
//        - if ct >= 35            -> 0            (hard override)
//        - otherwise use the label from calculate_ctvalue.js:
//            'Deficient' | 'Slightly Deficient' | 'Normal'
//          and convert it via scfas_9P_scoreMapping.
//   2. All species scores are summed (X).
//   3. X is compared against the conditions to get the result label:
//        X <= 6.5           -> Below optimum
//        6.5 < X < 8.05     -> Slightly below optimum
//        X >= 8.05          -> Optimum
//   4. The result is shown on page 21 (eng) / page 20 (thai).
// =====================================================================

// Species that are summed for this section.
// NOTE: these keys must match the CSV column / formData keys exactly.
const scfas_9P_sp = [
  'Bifidobacterium bifidum',
  'Bifidobacterium breve',
  'Bifidobacterium lactis',
  'Bifidobacterium longum',
  'Lactobacillus acidophilus',
  'Lactobacillus gasseri',
  'Lactobacillus paracasei',
  'Lactobacillus reuteri',
  'Lactobacillus rhamnosus',
  'Bacteroides genus', // "bacteroides"
];

// ---------------------------------------------------------------------
// Per-species LABEL -> score mapping.
// (Applied only when ct < 35; ct >= 35 always scores 0 — see helper.)
// Values taken from the provided table:
//                          Deficient  Slightly deficient  Normal
//   Bifidobacterium bifidum  0.175    0.35                0.7
//   Bifidobacterium breve    0.175    0.35                0.7
//   Bifidobacterium lactis   0.175    0.35                0.7
//   Bifidobacterium longum   0.175    0.35                0.7
//   Lactobacillus acidophilus 0.225   0.45                0.9
//   Lactobacillus gasseri    0.225    0.45                0.9
//   Lactobacillus paracasei  0.225    0.45                0.9
//   Lactobacillus reuteri    0.225    0.45                0.9
//   Lactobacillus rhamnosus  0.225    0.45                0.9
//   Bacteroides genus        0.75     1.5                 3
// ---------------------------------------------------------------------
const scfas_9P_scoreMapping = {
  'Bifidobacterium bifidum': {
    'Deficient': 0.175,
    'Slightly Deficient': 0.35,
    'Normal': 0.7,
  },
  'Bifidobacterium breve': {
    'Deficient': 0.175,
    'Slightly Deficient': 0.35,
    'Normal': 0.7,
  },
  'Bifidobacterium lactis': {
    'Deficient': 0.175,
    'Slightly Deficient': 0.35,
    'Normal': 0.7,
  },
  'Bifidobacterium longum': {
    'Deficient': 0.175,
    'Slightly Deficient': 0.35,
    'Normal': 0.7,
  },
  'Lactobacillus acidophilus': {
    'Deficient': 0.225,
    'Slightly Deficient': 0.45,
    'Normal': 0.9,
  },
  'Lactobacillus gasseri': {
    'Deficient': 0.225,
    'Slightly Deficient': 0.45,
    'Normal': 0.9,
  },
  'Lactobacillus paracasei': {
    'Deficient': 0.225,
    'Slightly Deficient': 0.45,
    'Normal': 0.9,
  },
  'Lactobacillus reuteri': {
    'Deficient': 0.225,
    'Slightly Deficient': 0.45,
    'Normal': 0.9,
  },
  'Lactobacillus rhamnosus': {
    'Deficient': 0.225,
    'Slightly Deficient': 0.45,
    'Normal': 0.9,
  },
  'Bacteroides genus': {
    'Deficient': 0.75,
    'Slightly Deficient': 1.5,
    'Normal': 3,
  },
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — ENGLISH version (page 21).
// X = summed score across all species above.
// Adjust the Xpos/Ypos/image values to match your PDF layout.
// ---------------------------------------------------------------------
const scfas_9P_condition = {
  page: 20, // eng version page (same page as macronutrient)
  XposText: 500,
  YposText: 430,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 88,
  YposImage: 392,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 173,
  YBarStart: 437,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 6.5,
      score: 1,
      label: 'Below optimum',
      image: 'ac1',
    },
    {
      condition: (x) => x > 6.5 && x < 8.05,
      score: 2,
      label: 'Slightly below optimum',
      image: 'ac2',
    },
    {
      condition: (x) => x >= 8.05,
      score: 3,
      label: 'Optimum',
      image: 'ac3',
    },
  ],
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — THAI version (page 20).
// Same thresholds, Thai labels + Thai-page positions.
// ---------------------------------------------------------------------
const scfas_9P_condition_thai = {
  page: 19, // thai version page (same page as macronutrient)
  XposText: 510,
  YposText: 402,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 87,
  YposImage: 368,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 175,
  YBarStart: 412,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 6.5,
      score: 1,
      label: 'ต่ำกว่าระดับที่เหมาะสม',
      image: 'ac1',
    },
    {
      condition: (x) => x > 6.5 && x < 8.05,
      score: 2,
      label: 'ต่ำกว่าระดับที่เหมาะสมเล็กน้อย',
      image: 'ac2',
    },
    {
      condition: (x) => x >= 8.05,
      score: 3,
      label: 'เหมาะสม',
      image: 'ac3',
    },
  ],
};

// ---------------------------------------------------------------------
// Helper: score for one species.
//   - ct >= 35            -> 0 (hard override)
//   - otherwise: label from calculate_ctvalue.js -> score via mapping
// Returns 0 when the species/label is unknown or the value is missing.
// ---------------------------------------------------------------------
function getScfas9PSpeciesScore(species, formData) {
  const raw = parseFloat(formData?.[species]);
  if (isNaN(raw)) return 0;

  // hard override: ct >= 35 always scores 0
  if (raw >= 35) return 0;

  const meta = bacteriaMetaConfig_9P[species];
  if (!meta) return 0;

  // label computed by the same logic as the rest of the 9P report
  const { label } = getBacteriaScore(species, raw, formData, meta);

  const mapping = scfas_9P_scoreMapping[species];
  if (!mapping || mapping[label] === undefined) return 0;

  return mapping[label];
}

// ---------------------------------------------------------------------
// Main: sum all species scores and resolve the result label.
// Pass useThaiVersion=true to use the Thai config (page 20).
// Returns: { sum, label, image, score, config }
// ---------------------------------------------------------------------
function calculateScfas9P(formData, useThaiVersion = false) {
  let sum = 0;

  scfas_9P_sp.forEach((species) => {
    sum += getScfas9PSpeciesScore(species, formData);
  });

  // round to 4 decimals to avoid float noise
  sum = parseFloat(sum.toFixed(4));

  const config = useThaiVersion
    ? scfas_9P_condition_thai
    : scfas_9P_condition;

  const matched = config.conditions.find((c) => c.condition(sum));

  return {
    sum,
    label: matched ? matched.label : null,
    image: matched ? matched.image : null,
    score: matched ? matched.score : null,
    config,
  };
}

export {
  scfas_9P_sp,
  scfas_9P_scoreMapping,
  scfas_9P_condition,
  scfas_9P_condition_thai,
  getScfas9PSpeciesScore,
  calculateScfas9P,
};
